import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      if (attempts > 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="pastel-grain min-h-screen grid place-items-center px-4">
      <div data-testid="payment-success-card"
        className="brutal-border brutal-shadow rounded-2xl p-8 bg-white max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin" size={48} />
            <h1 className="font-display text-2xl font-black mt-4">Verifying payment...</h1>
            <p className="text-zinc-600 font-medium mt-2">This takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto w-16 h-16 brutal-border rounded-full grid place-items-center" style={{ background: "var(--green)" }}>
              <CheckCircle2 size={32} strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-3xl font-black mt-4" data-testid="payment-success-title">Payment complete!</h1>
            <p className="text-zinc-600 font-medium mt-2">Your membership is active. Thank you!</p>
            <Link to="/">
              <Button className="mt-6 brutal-border brutal-shadow rounded-xl font-bold"
                style={{ background: "var(--primary)", color: "var(--ink)" }}>
                Back to forum
              </Button>
            </Link>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <div className="mx-auto w-16 h-16 brutal-border rounded-full grid place-items-center" style={{ background: "var(--destructive)" }}>
              <XCircle size={32} strokeWidth={2.5} className="text-white" />
            </div>
            <h1 className="font-display text-2xl font-black mt-4">{status === "timeout" ? "Timed out" : "Payment failed"}</h1>
            <p className="text-zinc-600 font-medium mt-2">Please try again or contact us if the issue persists.</p>
            <Link to="/membership">
              <Button className="mt-6 brutal-border brutal-shadow rounded-xl font-bold bg-white hover:bg-white text-[var(--ink)]">
                Try again
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="pastel-grain min-h-screen grid place-items-center px-4">
      <div data-testid="payment-cancel-card"
        className="brutal-border brutal-shadow rounded-2xl p-8 bg-white max-w-md w-full text-center">
        <h1 className="font-display text-3xl font-black">Payment cancelled</h1>
        <p className="text-zinc-600 font-medium mt-2">No charges were made.</p>
        <Link to="/membership">
          <Button className="mt-6 brutal-border brutal-shadow rounded-xl font-bold"
            style={{ background: "var(--primary)", color: "var(--ink)" }}>
            Back to plans
          </Button>
        </Link>
      </div>
    </div>
  );
}
